import React, { Suspense, useEffect, useMemo } from 'react';
import { Routes, Route, useLocation, matchRoutes } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOpenTabs } from '../context/OpenTabsContext';
import { ROUTE_CONFIG, isKeepAliveExcluded } from '../routes/routeConfig';
import ErrorBoundary from './ErrorBoundary';
import { KeepAliveActiveProvider } from '../context/KeepAliveActiveContext';
import LoadingFallback from './LoadingFallback';
import Navbar from './Navbar';
import ShortcutNavBar from './ShortcutNavBar';

// ---------------------------------------------------------------------------
// Build the matchable route list once (module-level constant — never changes)
// ---------------------------------------------------------------------------

// matchRoutes needs route objects with a `path` field.
// RouteObject from react-router-dom satisfies AgnosticRouteObject and also
// carries our `element` field so we can retrieve it from match.route.element.
const MATCHABLE_ROUTES = ROUTE_CONFIG.map((entry) => ({
  path: entry.path,
  // element stored here is recovered via match.route after matchRoutes()
  element: entry.element,
}));

// ---------------------------------------------------------------------------
// Helper: resolve element for a given full path string
// ---------------------------------------------------------------------------

function resolveRouteEntry(
  fullPath: string
): { configPath: string; element: React.ReactElement } | null {
  // fullPath is pathname + search (e.g. "/items?q=5dvk100")
  // matchRoutes works on pathname; search is ignored by path matching
  const pathname = fullPath.split('?')[0];

  const matches = matchRoutes(MATCHABLE_ROUTES, pathname);
  if (!matches || matches.length === 0) return null;

  // Take the deepest (last) match — handles nested routes if any
  const deepest = matches[matches.length - 1];
  const route = deepest.route as typeof MATCHABLE_ROUTES[number];
  if (!route.element) return null;

  return { configPath: route.path, element: route.element };
}

// ---------------------------------------------------------------------------
// KeepAliveOutlet
// ---------------------------------------------------------------------------

/**
 * Renders every open tab's screen component simultaneously, showing only the
 * active one. Hidden instances use `display:none` — they are NEVER unmounted
 * while their tab remains open, preserving all in-memory React state.
 *
 * Each instance gets its own `<Routes location={frozenLocation}>` so that
 * `useParams` / `useLocation` resolve correctly per the tab's frozen path,
 * while `useNavigate` still targets the outer Router (global navigation).
 */
const KeepAliveOutlet: React.FC = () => {
  const { user } = useAuth();
  const { tabs } = useOpenTabs();
  const location = useLocation();
  const activePath = location.pathname + location.search;

  useEffect(() => {
    if (isKeepAliveExcluded(location.pathname) || !user) return;
    let cancelled = false;
    let timer: number | undefined;
    let attempts = 0;

    const focusActiveHeaderSearch = () => {
      if (cancelled) return;
      attempts += 1;

      const activeTab = Array.from(document.querySelectorAll<HTMLElement>('[data-tab-path]'))
        .find((el) => el.dataset.tabPath === activePath);
      const input = activeTab?.querySelector<HTMLInputElement>(
        '[data-header-search-focus="true"] input'
      );

      if (input) {
        input.focus({ preventScroll: true });
        input.select();
        return;
      }

      if (attempts < 20) {
        timer = window.setTimeout(focusActiveHeaderSearch, 100);
      }
    };

    timer = window.setTimeout(focusActiveHeaderSearch, 0);

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [activePath, tabs.length, location.pathname, user]);

  // Pre-resolve each tab's config entry. We memo by the tab list to avoid
  // recalculating on every render (e.g., active path changes).
  const resolvedTabs = useMemo(() => {
    return tabs.map((tab) => {
      if (isKeepAliveExcluded(tab.path.split('?')[0])) {
        return { tab, resolved: null };
      }
      const resolved = resolveRouteEntry(tab.path);
      return { tab, resolved };
    });
  }, [tabs]);

  if (isKeepAliveExcluded(location.pathname) || !user) {
    return null;
  }

  if (tabs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex overflow-hidden">
        <div className="flex-1 flex flex-col w-full max-w-full overflow-hidden">
          <Navbar />
          {/* With zero open tabs no screen is mounted, and the bookmarks bar is
              rendered by the screens themselves — so it has to be drawn here as
              well. Without it, closing the last tab wipes the bar off the page
              and reads as "my bookmarks were deleted". */}
          <main className="flex-1 bg-gray-100 flex flex-col py-1 px-4 mt-12">
            <ShortcutNavBar />
          </main>
        </div>
      </div>
    );
  }

  return (
    <>
      {resolvedTabs.map(({ tab, resolved }) => {
        if (!resolved) return null;

        const isActive = tab.path === activePath;

        // Build a frozen Location object so that useLocation() inside the
        // screen returns the correct path, search, and any state (e.g. the
        // NonMaterialRouter reads location.state.code / .name).
        const tabPathname = tab.path.split('?')[0];
        const tabSearch = tab.path.includes('?') ? '?' + tab.path.split('?')[1] : '';
        const frozenLocation = {
          pathname: tabPathname,
          search: tabSearch,
          hash: '',
          state: tab.state ?? null,
          key: tab.id,
        };

        return (
          <div
            key={tab.id}
            style={isActive ? undefined : { display: 'none' }}
            data-tab-id={tab.id}
            data-tab-path={tab.path}
          >
            <KeepAliveActiveProvider value={isActive}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <Routes location={frozenLocation}>
                  <Route path={resolved.configPath} element={resolved.element} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            </KeepAliveActiveProvider>
          </div>
        );
      })}
    </>
  );
};

export default KeepAliveOutlet;
